/**
 * SF-T-271: the AI's suggested order for a parent's sub-tasks (Suggest the order), read the same way by the hub (is it
 * still for the sub-tasks as they are; the note a run's suggestion posts) and sf-agent (what it prints). Type-only
 * imports: sf-agent compiles this file as it is.
 */
import type { SubtaskOrderChange, SubtaskOrderEntry, SubtaskOrderSuggestion } from './types/tasks';

/** The same sub-tasks, in the same order, each naming the same ones and with the same break. */
export function sameOrderGraph(a: readonly SubtaskOrderEntry[], b: readonly SubtaskOrderEntry[]): boolean {
  if (a.length !== b.length) return false;
  const key = (e: SubtaskOrderEntry) => `${e.task_id}|${[...e.after].sort().join(',')}|${e.chain_break ? 1 : 0}`;
  return a.every((e, i) => key(e) === key(b[i]));
}

/**
 * SF-T-268: the sub-tasks (ids) a suggestion's list order moves: those whose place differs from the list it read
 * (`graph`). None without an order. A built or done one may not move (it would change what the ones after it wait on).
 */
export function orderMoves(suggestion: Pick<SubtaskOrderSuggestion, 'graph' | 'order'>): string[] {
  if (!suggestion.order?.length) return [];
  return suggestion.order.filter((o, i) => o.task_id !== suggestion.graph[i]?.task_id).map((o) => o.task_id);
}

/** What one change does, in a line: "SF-T-14 waits on SF-T-12 (was SF-T-13); a break". */
export function orderChangeText(change: SubtaskOrderChange, ref: (n: number) => string): string {
  const names = (after: SubtaskOrderChange['after']) => (after.length ? after.map((a) => ref(a.number)).join(', ') : 'the one before it in the list');
  const parts: string[] = [];
  const was = new Set(change.was_after.map((a) => a.task_id));
  const sameAfter = was.size === change.after.length && change.after.every((a) => was.has(a.task_id));
  if (!sameAfter) parts.push(`waits on ${names(change.after)} (was ${names(change.was_after)})`);
  if (change.chain_break !== change.was_break) parts.push(change.chain_break ? 'stops here for a person' : 'no longer stops here for a person');
  return `${ref(change.number)} ${parts.join('; ')}`;
}

/** How many things it changes: each sub-task it changes, and the list's order when it moves rows. */
export function orderSuggestionCount(suggestion: Pick<SubtaskOrderSuggestion, 'changes' | 'order'>): number {
  return suggestion.changes.length + (suggestion.order?.length ? 1 : 0);
}

/**
 * The suggestion as a chat's `order` change (factory-changes), for the chat's agent to propose: each sub-task it changes
 * with what it would wait on and its break, and the list's order when it moves them.
 */
export function orderSuggestionChange(suggestion: SubtaskOrderSuggestion, parentRef: string, ref: (n: number) => string) {
  return {
    id: 'order',
    action: 'order' as const,
    task: parentRef,
    subtasks: suggestion.changes.map((c) => ({ task: ref(c.number), after: c.after.map((a) => ref(a.number)), chain_break: c.chain_break })),
    ...(suggestion.order?.length ? { order: suggestion.order.map((o) => ref(o.number)) } : {}),
    ...(suggestion.summary ? { why: suggestion.summary } : {}),
  };
}

/** The whole suggestion as text: for a run's note on the task, and for sf-agent to print. */
export function orderSuggestionText(suggestion: SubtaskOrderSuggestion, parentRef: string, ref: (n: number) => string): string {
  const n = orderSuggestionCount(suggestion);
  if (!n) return [`The AI read ${parentRef}'s sub-tasks and suggests no changes to their order.`, suggestion.summary].filter(Boolean).join('\n\n');
  const lines = suggestion.changes.map((c) => `- ${orderChangeText(c, ref)}${c.why ? `: ${c.why}` : ''}`);
  if (suggestion.order?.length) lines.unshift(`- The list in this order: ${suggestion.order.map((o) => ref(o.number)).join(', ')}`);
  return [
    `The AI suggests ${n === 1 ? 'a change' : `${n} changes`} to the order of ${parentRef}'s sub-tasks. Nothing is applied: accept ${n === 1 ? 'it' : 'them'} in one go, or dismiss ${n === 1 ? 'it' : 'them'}, in the task's sub-task list.`,
    suggestion.summary,
    lines.join('\n'),
  ]
    .filter(Boolean)
    .join('\n\n');
}
